import React from 'react';
import { Box, TextV2, TooltipV2 } from '@withjoy/joykit';
import { Undo } from '@assets/icons';
import { useTranslation } from '@shared/core';
import { animationTransition } from '@shared/utils/animationTransition';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { useDesignGalleryFiltersContext } from '../../../DesignGalleryFiltersProvider';

/**
 * Icon-only reset: a circular pill with the counter-clockwise `Undo` icon (joykit's `Restart`
 * turns clockwise and read as "refresh"). "Reset All Filters" is the accessible name and the
 * tooltip, styled like the colorway swatch tooltips. Clears every `pf` facet and the
 * `?format=` display param. Renders nothing when no filter is active.
 */
export const ResetAllFiltersButton = ({ pillHeight = 40 }: { pillHeight?: number }) => {
  const { tq } = useTranslation('stationery');
  const { buttonInteracted } = useCardTelemetry();
  const { clearAllFilters, getTotalActiveFilterCount } = useDesignGalleryFiltersContext();
  const activeFiltersCount = getTotalActiveFilterCount();

  if (activeFiltersCount === 0) return null;

  return (
    <TooltipV2
      content={<TextV2>{tq`Reset All Filters`}</TextV2>}
      placement="top"
      onMouseLeaveDelay={0}
    >
      <Box
        onClick={() => {
          buttonInteracted({ name: 'galleryResetFilters', activeFiltersCount });
          clearAllFilters();
        }}
        backgroundColor="mono2"
        color="mono14"
        as="button"
        type="button"
        flex="none"
        aria-label={tq`Reset All Filters`}
        height={pillHeight}
        width={pillHeight}
        padding={0}
        borderRadius="50%"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        transition={animationTransition('background-color')}
        data-testid="reset-all-filters"
      >
        <Undo aria-hidden="true" />
      </Box>
    </TooltipV2>
  );
};
